import { Box, Card, CardActionArea, Typography as T } from "@mui/material";
import { makeStyles } from "@mui/styles";

import { reduceIconPath, resolveVehicleImagePath } from "helpers/functions";
import { Car } from "helpers/global-types";
import { useCurrency, useTypedTheme } from "hooks";
import { useStoreActions } from "store/store";
import { palette } from "theme/palette";

const useStyles = makeStyles(() => {
  const { theme } = useTypedTheme();

  const { breakpoints } = theme;

  return {
    carCard: {
      minWidth: "230px",
      border: `1px solid ${palette.grey}`,
      borderRadius: "10px",
      flexGrow: 1,
      maxWidth: "300px",
      [breakpoints.down("sm")]: {
        flexBasis: "100%",
        maxWidth: "380px",
      },
    },
    carCardActive: {
      border: `1px solid ${palette.accent}`,
    },
  };
});

type Props = {
  cardData: Car;
  setSelectedCar: (car: Car) => void;
  activeCar: Car;
};
export const CarCard = ({ cardData, setSelectedCar, activeCar }: Props) => {
  // styles
  const classes = useStyles();

  // actions
  const setStoreSelectedCart = useStoreActions((a) => a.setSelectedCar);
  const setSelectedAddons = useStoreActions((a) => a.setSelectedAddons);
  const { formatPrice } = useCurrency();

  const {
    carName,
    price,
    numberOfSeats,
    picturePath,
    vehicleId,
    description,
    tripType,
  } = cardData;
  const isCarActive = String(vehicleId) === String(activeCar?.vehicleId);
  const resolvedImageUrl = resolveVehicleImagePath(picturePath);

  const cardClick = () => {
    setSelectedAddons([]);
    setStoreSelectedCart(cardData);
    setSelectedCar(cardData);
  };

  return (
    <Card
      elevation={0}
      className={
        isCarActive
          ? [classes.carCard, classes.carCardActive].join(" ")
          : classes.carCard
      }
    >
      <CardActionArea
        onClick={cardClick}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <Box
          sx={{
            width: "100%",
            minHeight: "150px",
            display: "flex",
            marginBottom: "30px",
            justifyContent: "center",
            backgroundImage: resolvedImageUrl
              ? `url(${resolvedImageUrl})`
              : "none",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            padding: "20px",
            paddingTop: "0",
            boxSizing: "border-box",
          }}
        >
          <T variant="cardLabelText">{carName}</T>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <img
              src={reduceIconPath("Person.svg")}
              style={{ marginRight: "10px", width: "16px", height: "16px" }}
            />
            <T variant="cardSeatsText">
              {numberOfSeats}&nbsp;{numberOfSeats === 1 ? "Seat" : "Seats"}
            </T>
          </Box>
        </Box>
        {tripType && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              padding: "20px",
              paddingTop: "0",
              boxSizing: "border-box",
              textTransform: "capitalize",
            }}
          >
            <T variant="cardTripTypeText">{tripType}</T>
          </Box>
        )}
        {description && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              padding: "20px",
              paddingTop: "0",
              boxSizing: "border-box",
              fontSize: "11px",
              "& ul": {
                padding: 0,
                margin: 0,
                listStyleType: "none",
              },
              "& ul li": {
                marginBottom: "5px",
              },
              "& ul li:last-of-type": {
                marginBottom: "0",
              },
            }}
            dangerouslySetInnerHTML={{ __html: description }}
          ></Box>
        )}
        <Box
          sx={{
            width: "100%",
            height: "46px",
            backgroundColor: isCarActive ? palette.accent : palette.grey,
            display: "flex",
            justifyContent: "center",
            textAlign: "center",
            marginTop: "auto",
          }}
        >
          <T
            variant="cardFooter"
            sx={{
              color: isCarActive ? palette.white : palette.black,
              alignSelf: "center",
            }}
          >
            {isCarActive ? "Vehicle Selected" : "Select Vehicle"}
          </T>
        </Box>
        <Box
          sx={{
            position: "absolute",
            backgroundColor: isCarActive ? palette.accent : palette.grey,
            top: 0,
            left: 0,
            padding: "10px",
          }}
        >
          <T
            variant="h3"
            sx={{
              fontSize: "20px",
              lineHeight: "23px",
              color: isCarActive ? palette.white : palette.black,
              alignSelf: "center",
            }}
          >
            {formatPrice(price)}
          </T>
        </Box>
      </CardActionArea>
    </Card>
  );
};
